import React, { FunctionComponent } from 'react';
import { InjectedIntl } from 'react-intl';
import { Row, Col } from 'react-flexbox-grid';
import { Button, IconButton } from '@nbc-design/button';
import { ArrowLeft } from '@nbc-design/icons/lib/web/ArrowLeft';
import { Tooltip } from '@nbc-design/tooltip';

import { ConfigData } from 'types/interfaces';
import { getTextFactory } from 'utils/TextUtils';

export type FormFooterProps = {
  intl: InjectedIntl;
  goToPreviousStep?: (() => void) | null;
  config?: ConfigData;
  disableSubmit?: boolean;
  onBeforeSubmit?: (e: React.MouseEvent) => void;
  backBlocked?: boolean;
  /** Tooltip text shown on the disabled back button. Defaults to leftMenu.isBlockedStep. */
  backBlockedTooltip?: string;
};

const FormFooter: FunctionComponent<FormFooterProps> = (props: FormFooterProps) => {
  const {
    intl,
    goToPreviousStep = null,
    disableSubmit = false,
    config = {},
    onBeforeSubmit,
    backBlocked = false,
    backBlockedTooltip,
  } = props;

  const getText = getTextFactory(intl, 'global.button');
  const getAccessibilityText = getTextFactory(intl, 'accessibility.button');
  const getLeftMenuText = getTextFactory(intl, 'leftMenu');

  let buttonKey = 'continue';
  buttonKey = config.BUTTON_SUBMIT ? 'submit' : buttonKey;
  buttonKey = config.BUTTON_SIGN ? 'sign' : buttonKey;
  const buttonText = getText(buttonKey);

  const renderBackButton = () => {
    if (!goToPreviousStep && !backBlocked) return <div />;

    const button = (
      <IconButton
        icon={<ArrowLeft title={getAccessibilityText('back')} />}
        onClick={backBlocked ? undefined : () => goToPreviousStep!()}
        type="button"
        appearance="secondary"
        className={`back-button [&_svg]:w-6 [&_svg]:h-6${backBlocked ? ' [&_svg_path]:fill-current' : ''}`}
        disabled={backBlocked}
      />
    );

    if (backBlocked) {
      const tooltipContent = backBlockedTooltip ?? getLeftMenuText('isBlockedStep');
      return (
        <Tooltip id="back-button-blocked" position="bottom" content={<div>{tooltipContent}</div>}>
          {button}
        </Tooltip>
      );
    }

    return button;
  };

  return (
    <Row className="!pb-0">
      <Col
        xs={12}
        md={12}
        lg={12}
        className={`flex justify-between w-full${
          config.FOOTER_DIVIDER === false ? '' : ' border-t border-solid border-grey-100 pt-10'
        } pb-4 mt-8`}
        data-test="buttons__form_footer"
      >
        {renderBackButton()}
        {config.BUTTON_CONTINUE === false ? null : (
          <Button
            appearance="primary"
            type={onBeforeSubmit ? 'button' : 'submit'}
            disabled={disableSubmit}
            dataTest={buttonKey}
            onClick={onBeforeSubmit}
          >
            {buttonText}
          </Button>
        )}
      </Col>
    </Row>
  );
};

export default FormFooter;
